
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { Property } from '@repo/shared-types';
import { useToast } from '../../components/common/ToastContext';
import { propertySchema, unitSchema, portfolioSchema } from '@repo/shared-types/property/schemas';
import { entityNoteListItemSchema } from '@repo/shared-types/common/noteSchemas';
import type { Note } from '@repo/shared-types/common/notes';
import { validateEntity, validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';

export const useProperties = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['properties', filters ?? {}],
    queryFn: async () => {
      const raw = await api.properties.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Property[] }).data;
      const validation = validateEntityArray(propertySchema, list);
      if (!validation.valid) {
        logger.error('Invalid properties query response:', validation.errors);
        throw new Error('Invalid properties data received from API');
      }
      return validation.data! as Property[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const usePropertiesPaginated = (filters: Record<string, string>) => {
  return useQuery({
    queryKey: ['properties', 'paginated', filters],
    queryFn: async () => {
      const raw = await api.properties.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Property[] }).data;
      const total = Array.isArray(raw) ? raw.length : (raw as { data: Property[]; total: number }).total;
      const validation = validateEntityArray(propertySchema, list);
      if (!validation.valid) {
        logger.error('Invalid properties query response:', validation.errors);
        throw new Error('Invalid properties data received from API');
      }
      return { data: validation.data! as Property[], total };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useProperty = (id: string | undefined) => {
  return useQuery({
    queryKey: ['properties', id],
    queryFn: async () => {
      const data = await api.properties.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(propertySchema, data);
        if (!validation.valid) {
          logger.error('Invalid property query response:', validation.errors);
          throw new Error('Invalid property data received from API');
        }
        return validation.data! as Property;
      }
      return null;
    },
    enabled: !!id,
  });
};

export const usePropertyFloorPlans = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ['properties', propertyId, 'floorplans'],
    queryFn: () => api.properties.listFloorPlans(propertyId!),
    enabled: !!propertyId?.trim(),
    staleTime: 2 * 60 * 1000,
  });
};

export const useUnits = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['units', filters ?? {}],
    queryFn: async () => {
      const raw = await api.units.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: import('types').Unit[] }).data;
      const validation = validateEntityArray(unitSchema, list);
      if (!validation.valid) {
        logger.error('Invalid units query response:', validation.errors);
        throw new Error('Invalid units data received from API');
      }
      return validation.data! as import('types').Unit[];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useUnitsPaginated = (filters: Record<string, string>) => {
  return useQuery({
    queryKey: ['units', 'paginated', filters],
    queryFn: async () => {
      const raw = await api.units.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: import('types').Unit[] }).data;
      const total = Array.isArray(raw) ? raw.length : (raw as { data: import('types').Unit[]; total: number }).total;
      const validation = validateEntityArray(unitSchema, list);
      if (!validation.valid) {
        logger.error('Invalid units query response:', validation.errors);
        throw new Error('Invalid units data received from API');
      }
      return { data: validation.data! as import('types').Unit[], total };
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const usePortfolios = () => {
  return useQuery({
    queryKey: ['properties', 'portfolios'],
    queryFn: async () => {
      const data = await api.properties.portfolios.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(portfolioSchema, data);
      if (!validation.valid) {
        logger.error('Invalid portfolios query response:', validation.errors);
        throw new Error('Invalid portfolios data received from API');
      }
      return validation.data! as import('@repo/shared-types/property/schemas').Portfolio[];
    },
  });
};

export const useCreatePortfolio = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (portfolioData: { name: string; properties: string[]; image?: string }) =>
      api.properties.portfolios.create({
        name: portfolioData.name,
        propertyIds: portfolioData.properties,
        // image is not supported by API, description can be added later if needed
      }),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(portfolioSchema, data);
      if (!validation.valid) {
        logger.error('Invalid portfolio response from API:', validation.errors);
        showToast('Portfolio created but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['properties', 'portfolios'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['properties', 'portfolios'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Portfolio created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create portfolio', 'error');
    }
  });
};

export const useUpdatePortfolio = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { name?: string; properties?: string[]; image?: string } }) => {
      const { properties, image: _image, ...rest } = updates;
      return api.properties.portfolios.update(id, {
        ...rest,
        ...(properties && { propertyIds: properties }),
      } as Partial<{ name: string; description?: string; propertyIds: string[] }>);
    },
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(portfolioSchema, data);
      if (!validation.valid) {
        logger.error('Invalid portfolio response from API:', validation.errors);
        showToast('Portfolio updated but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['properties', 'portfolios'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['properties', 'portfolios'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Portfolio updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update portfolio', 'error');
    }
  });
};

export const useDeletePortfolio = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.properties.portfolios.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties', 'portfolios'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Portfolio deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete portfolio', 'error');
    }
  });
};

export const useCreateProperty = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (newProperty: Property) => api.properties.create(newProperty),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      if (data) {
        const validation = validateEntity(propertySchema, data);
        if (!validation.valid) {
          logger.error('Invalid property response from API:', validation.errors);
          showToast('Property created but received invalid data from server', 'error');
          queryClient.invalidateQueries({ queryKey: ['properties'] });
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Property onboarded successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create property', 'error');
    }
  });
};

export const useUpdateProperty = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Property> }) => api.properties.update(id, updates),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      if (data) {
        const validation = validateEntity(propertySchema, data);
        if (!validation.valid) {
          logger.error('Invalid property update response:', validation.errors);
          showToast('Property updated but received invalid data from server', 'error');
          throw new Error('Invalid property data received from API');
        }
        queryClient.invalidateQueries({ queryKey: ['properties', data.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Property updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update property', 'error');
    }
  });
};

export const useDeleteProperty = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.properties.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Property deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete property', 'error');
    }
  });
};

export const useTransferToOwner = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.properties.transferToOwner(id),
    onSuccess: (data) => {
      if (data) {
        const validation = validateEntity(propertySchema, data);
        if (validation.valid) queryClient.setQueryData(['properties', data.id], validation.data);
      }
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Property transferred to owner (self-managed)', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to transfer to owner', 'error');
    }
  });
};

export const useAssignPm = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, propertyManagerId, inviteCode }: { id: string; propertyManagerId?: string; inviteCode?: string }) =>
      api.properties.assignPm(id, { propertyManagerId, inviteCode }),
    onSuccess: (data) => {
      if (data) {
        const validation = validateEntity(propertySchema, data);
        if (validation.valid) queryClient.setQueryData(['properties', data.id], validation.data);
      }
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Property manager assigned', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to assign property manager', 'error');
    }
  });
};

// --- PROPERTY NOTES ---
export const usePropertyNotes = (propertyId: string) => {
  return useQuery({
    queryKey: ['properties', 'notes', propertyId],
    queryFn: async () => {
      const data = await api.properties.notes.list(propertyId);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(entityNoteListItemSchema, data);
      if (!validation.valid) {
        logger.error('Invalid property notes query response:', validation.errors);
        return [] as Note[];
      }
      return validation.data! as Note[];
    },
    enabled: !!propertyId,
  });
};

export const useCreatePropertyNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (note: { propertyId: string; content: string; category?: import('@repo/shared-types/common/notes').NoteCategory; isImportant?: boolean }) =>
      api.properties.notes.create(note.propertyId, { content: note.content, category: note.category, isImportant: note.isImportant }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['properties', 'notes', variables.propertyId] });
      showToast('Note created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create note', 'error');
    }
  });
};

export const useUpdatePropertyNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, propertyId, updates }: { id: string; propertyId: string; updates: { content: string; category?: import('@repo/shared-types/common/notes').NoteCategory; isImportant?: boolean } }) =>
      api.properties.notes.update(propertyId, id, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['properties', 'notes'] });
      queryClient.invalidateQueries({ queryKey: ['properties', 'notes', variables.propertyId] });
      showToast('Note updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update note', 'error');
    }
  });
};

export const useDeletePropertyNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, propertyId }: { id: string; propertyId: string }) => api.properties.notes.delete(propertyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties', 'notes'] });
      showToast('Note deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete note', 'error');
    }
  });
};

export const useCreateUnit = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (unit: Omit<import('types').Unit, 'id' | 'floorPlanId'> & { floorPlanId?: string }) =>
      api.units.create(unit),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      if (data) {
        const validation = validateEntity(unitSchema, data);
        if (!validation.valid) {
          logger.error('Invalid unit response from API:', validation.errors);
          showToast('Unit created but received invalid data from server', 'error');
          queryClient.invalidateQueries({ queryKey: ['units'] });
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Unit created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create unit', 'error');
    }
  });
};

export const useUpdateUnit = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<import('types').Unit> }) => api.units.update(id, updates),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      if (data) {
        const validation = validateEntity(unitSchema, data);
        if (!validation.valid) {
          logger.error('Invalid unit response from API:', validation.errors);
          showToast('Unit updated but received invalid data from server', 'error');
          queryClient.invalidateQueries({ queryKey: ['units'] });
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Unit updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update unit', 'error');
    }
  });
};

export const useDeleteUnit = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.units.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showToast('Unit deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete unit', 'error');
    }
  });
};


